import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import {
  contentTypeDefs,
  generateAIContent,
  simulateGeneration,
  type AIContentType,
  type AIWizardHistoryEntry,
} from '@/mocks/aiRadioWizard';

export default function SAAIRadio() {
  const [selectedType, setSelectedType] = useState<AIContentType | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [editedResult, setEditedResult] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [history, setHistory] = useState<AIWizardHistoryEntry[]>([]);
  const [historySelectedId, setHistorySelectedId] = useState<string | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const getTypeDef = useCallback(
    (type: AIContentType) => contentTypeDefs.find((d) => d.id === type)!,
    [],
  );

  const handleSelectType = (type: AIContentType) => {
    const def = getTypeDef(type);
    const initial: Record<string, string> = {};
    def.fields.forEach((f) => {
      if (f.type === 'select') {
        initial[f.id] = f.options?.[0] || '';
      } else {
        initial[f.id] = '';
      }
    });
    initial.radio = def.defaultRadio;
    if (def.defaultTone && def.fields.some((f) => f.id === 'tono')) {
      initial.tono = def.defaultTone;
    }
    setSelectedType(type);
    setFormValues(initial);
    setResult(null);
    setEditedResult('');
    setIsEditing(false);
  };

  const handleInputChange = (fieldId: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleGenerate = async () => {
    if (!selectedType) return;
    setIsGenerating(true);
    setResult(null);
    setEditedResult('');
    setIsEditing(false);

    await simulateGeneration();

    const content = generateAIContent(selectedType, formValues);
    setResult(content);
    setEditedResult(content);

    const entry: AIWizardHistoryEntry = {
      id: `h${Date.now()}`,
      type: selectedType,
      typeLabel: getTypeDef(selectedType).label,
      radioName: formValues.radio || getTypeDef(selectedType).defaultRadio,
      inputs: { ...formValues },
      result: content,
      timestamp: new Date().toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
    };
    setHistory((prev) => [entry, ...prev]);
    setHistorySelectedId(entry.id);
    setIsGenerating(false);

    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
  };

  const handleCopy = async () => {
    const text = isEditing ? editedResult : result;
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      Swal.fire({
        icon: 'success',
        title: 'Copiado',
        text: 'El contenido se copió al portapapeles.',
        timer: 1500,
        showConfirmButton: false,
        customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold' },
      });
    } catch {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo copiar. Intentá de nuevo.',
        confirmButtonColor: '#1a1a1a',
        customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl' },
      });
    }
  };

  const handleSaveEdit = () => {
    setResult(editedResult);
    setIsEditing(false);
    setHistory((prev) =>
      prev.map((h) => (h.id === historySelectedId ? { ...h, result: editedResult } : h)),
    );
    Swal.fire({
      icon: 'success',
      title: 'Guardado',
      text: 'El contenido fue actualizado.',
      timer: 1500,
      showConfirmButton: false,
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold' },
    });
  };

  const handleCancelEdit = () => {
    setEditedResult(result || '');
    setIsEditing(false);
  };

  const handleRegenerate = () => {
    handleGenerate();
  };

  const handleLoadFromHistory = (entry: AIWizardHistoryEntry) => {
    setSelectedType(entry.type);
    setFormValues(entry.inputs);
    setResult(entry.result);
    setEditedResult(entry.result);
    setIsEditing(false);
    setHistorySelectedId(entry.id);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const handleDeleteHistory = (id: string) => {
    Swal.fire({
      title: '¿Eliminar del historial?',
      text: 'Esta acción no se puede deshacer.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#9ca3af',
      customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl' },
    }).then((res) => {
      if (res.isConfirmed) {
        setHistory((prev) => prev.filter((h) => h.id !== id));
        if (historySelectedId === id) setHistorySelectedId(null);
      }
    });
  };

  const handleClearForm = () => {
    setSelectedType(null);
    setResult(null);
    setEditedResult('');
    setIsEditing(false);
    setFormValues({});
    setHistorySelectedId(null);
  };

  const def = selectedType ? getTypeDef(selectedType) : null;

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-4 max-w-[1600px] mx-auto">
            {/* Breadcrumbs */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">IA para Radios</span>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-[10px] font-semibold mb-2">
                  <i className="ri-robot-2-line text-xs"></i>
                  Bloque SA-18
                </div>
                <h2 className="text-foreground-950 font-bold text-lg">IA para Radios</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  Generá contenido rápido · Publicaciones, concursos, eventos, promociones, patrocinios y mensajes
                </p>
              </div>
              {selectedType && (
                <button
                  onClick={handleClearForm}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-background-50 border border-background-200 text-foreground-500 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line text-sm"></i>
                  Nuevo contenido
                </button>
              )}
            </div>

            {/* Main two-column layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* LEFT: Wizard */}
              <div className="lg:col-span-2 space-y-4">
                {/* Step 1: Content Type Selector */}
                {!selectedType && (
                  <div className="bg-background-50 border border-background-200 rounded-2xl p-5">
                    <p className="text-foreground-400 text-[10px] font-semibold uppercase tracking-wider mb-3">
                      ¿Qué querés generar?
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {contentTypeDefs.map((ct) => (
                        <button
                          key={ct.id}
                          onClick={() => handleSelectType(ct.id)}
                          className={`${ct.bg} border border-background-200 rounded-2xl p-4 text-left hover:border-foreground-300 transition-all cursor-pointer group`}
                        >
                          <div className={`w-9 h-9 rounded-xl ${ct.color} ${ct.bg} flex items-center justify-center mb-2.5 group-hover:scale-110 transition-transform`}>
                            <i className={`${ct.icon} text-lg`}></i>
                          </div>
                          <p className="text-foreground-800 text-xs font-bold">{ct.label}</p>
                          <p className="text-foreground-400 text-[10px] leading-relaxed mt-1 line-clamp-2">{ct.description}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 2: Form */}
                {selectedType && def && !result && !isGenerating && (
                  <div className="bg-background-50 border border-background-200 rounded-2xl p-5 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl ${def.bg} flex items-center justify-center`}>
                        <i className={`${def.icon} ${def.color} text-lg`}></i>
                      </div>
                      <div>
                        <p className="text-foreground-800 text-sm font-bold">{def.label}</p>
                        <p className="text-foreground-400 text-[10px]">{def.description}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {def.fields.map((field) => (
                        <div
                          key={field.id}
                          className={`${field.type === 'textarea' ? 'sm:col-span-2' : ''}`}
                        >
                          <label className="block text-foreground-600 text-[10px] font-semibold mb-1.5">
                            {field.label}
                          </label>
                          {field.type === 'select' ? (
                            <select
                              value={formValues[field.id] || ''}
                              onChange={(e) => handleInputChange(field.id, e.target.value)}
                              className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 outline-none focus:border-foreground-300 transition-colors cursor-pointer appearance-none"
                              style={{
                                backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24'%3E%3Cpath fill='%23999' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E\")",
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 10px center',
                                paddingRight: '2rem',
                              }}
                            >
                              {field.options?.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : field.type === 'textarea' ? (
                            <textarea
                              value={formValues[field.id] || ''}
                              onChange={(e) => handleInputChange(field.id, e.target.value)}
                              placeholder={field.placeholder}
                              rows={3}
                              maxLength={500}
                              className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors resize-y"
                            />
                          ) : (
                            <input
                              type="text"
                              value={formValues[field.id] || ''}
                              onChange={(e) => handleInputChange(field.id, e.target.value)}
                              placeholder={field.placeholder}
                              className="w-full px-3 py-2.5 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
                            />
                          )}
                        </div>
                      ))}
                    </div>

                    <button
                      onClick={handleGenerate}
                      disabled={Object.values(formValues).every((v) => !v.trim())}
                      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer disabled:opacity-30 whitespace-nowrap"
                    >
                      <i className="ri-magic-line text-sm"></i>
                      Generar contenido
                    </button>
                  </div>
                )}

                {/* Step 3: Generating */}
                {isGenerating && (
                  <div className="bg-background-50 border border-background-200 rounded-2xl p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-violet-50 flex items-center justify-center">
                      <i className="ri-loader-4-line text-violet-500 text-2xl animate-spin"></i>
                    </div>
                    <p className="text-foreground-800 text-sm font-bold">Generando contenido...</p>
                    <p className="text-foreground-400 text-[10px] mt-1">
                      {def ? `Creando ${def.label.toLowerCase()} con IA` : 'Procesando...'}
                    </p>
                    <div className="flex items-center justify-center gap-1 mt-4">
                      <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-1.5 h-1.5 rounded-full bg-foreground-400 animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                )}

                {/* Step 4: Result */}
                {result && !isGenerating && selectedType && def && (
                  <div ref={resultRef} className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                    {/* Result header */}
                    <div className="px-4 py-3 border-b border-background-200 flex items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className={`w-8 h-8 rounded-lg ${def.bg} flex items-center justify-center`}>
                          <i className={`${def.icon} ${def.color} text-sm`}></i>
                        </div>
                        <div>
                          <p className="text-foreground-800 text-xs font-semibold">
                            {def.label} generado
                          </p>
                          <p className="text-foreground-400 text-[9px]">
                            {formValues.radio || def.defaultRadio}
                          </p>
                        </div>
                      </div>
                      <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[9px] font-medium whitespace-nowrap">
                        <i className="ri-check-line text-[9px] mr-0.5"></i>Listo
                      </span>
                    </div>

                    {/* Result content */}
                    <div className="p-4">
                      {isEditing ? (
                        <textarea
                          value={editedResult}
                          onChange={(e) => setEditedResult(e.target.value)}
                          rows={14}
                          maxLength={2000}
                          className="w-full px-3 py-3 bg-background-100 border border-background-200 rounded-xl text-xs text-foreground-800 leading-relaxed outline-none focus:border-foreground-300 transition-colors resize-y"
                        />
                      ) : (
                        <div className="bg-background-100 rounded-xl p-4">
                          <p className="text-foreground-800 text-xs leading-relaxed whitespace-pre-line">
                            {result}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Result actions */}
                    <div className="px-4 py-3 border-t border-background-200 flex items-center gap-1.5 flex-wrap">
                      {isEditing ? (
                        <>
                          <button
                            onClick={handleSaveEdit}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-foreground-950 text-background-50 text-[9px] font-medium hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-check-line text-[9px]"></i>
                            Guardar
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-background-200 text-foreground-500 text-[9px] font-medium hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            Cancelar
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={handleCopy}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-background-200 text-foreground-500 text-[9px] font-medium hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-file-copy-line text-[9px]"></i>
                            Copiar
                          </button>
                          <button
                            onClick={() => { setIsEditing(true); setEditedResult(result); }}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-background-200 text-foreground-500 text-[9px] font-medium hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-edit-line text-[9px]"></i>
                            Editar
                          </button>
                          <button
                            onClick={handleRegenerate}
                            disabled={isGenerating}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-violet-50 text-violet-600 text-[9px] font-medium hover:bg-violet-100 transition-colors cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-refresh-line text-[9px]"></i>
                            Regenerar
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* RIGHT: History */}
              <div className="bg-background-50 border border-background-200 rounded-2xl flex flex-col h-[350px] lg:h-[640px]">
                <div className="px-4 py-3 border-b border-background-200 shrink-0">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-foreground-800 text-xs font-semibold">Historial</p>
                      <p className="text-foreground-400 text-[10px]">{history.length} generaciones</p>
                    </div>
                    {history.length > 0 && (
                      <button
                        onClick={() => {
                          Swal.fire({
                            title: '¿Limpiar historial?',
                            text: 'Se borrarán todas las generaciones.',
                            icon: 'question',
                            showCancelButton: true,
                            confirmButtonText: 'Limpiar',
                            cancelButtonText: 'Cancelar',
                            confirmButtonColor: '#6b7280',
                            cancelButtonColor: '#9ca3af',
                            customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl' },
                          }).then((res) => {
                            if (res.isConfirmed) {
                              setHistory([]);
                              setHistorySelectedId(null);
                            }
                          });
                        }}
                        className="text-foreground-300 hover:text-foreground-500 transition-colors cursor-pointer"
                        title="Limpiar historial"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {history.length === 0 ? (
                    <div className="text-center py-10">
                      <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-background-100 flex items-center justify-center">
                        <i className="ri-history-line text-foreground-300 text-xl"></i>
                      </div>
                      <p className="text-foreground-400 text-[10px] max-w-[180px] mx-auto">
                        Acá vas a ver todo el contenido que generes con IA
                      </p>
                    </div>
                  ) : (
                    <div className="divide-y divide-background-200">
                      {history.map((entry) => {
                        const entryDef = getTypeDef(entry.type);
                        return (
                          <div
                            key={entry.id}
                            onClick={() => handleLoadFromHistory(entry)}
                            className={`px-3 py-2.5 cursor-pointer transition-colors group ${
                              historySelectedId === entry.id
                                ? 'bg-background-200'
                                : 'hover:bg-background-100'
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              <div className={`w-7 h-7 rounded-lg ${entryDef.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                                <i className={`${entryDef.icon} ${entryDef.color} text-[11px]`}></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                  <span className="text-[10px] font-semibold text-foreground-800 truncate">
                                    {entryDef.label}
                                  </span>
                                </div>
                                <p className="text-[9px] text-foreground-500 truncate mt-0.5">
                                  {entry.radioName}
                                </p>
                                <p className="text-[9px] text-foreground-400 mt-0.5 line-clamp-2 leading-relaxed">
                                  {entry.result.slice(0, 80)}
                                </p>
                                <p className="text-[8px] text-foreground-300 mt-1">{entry.timestamp}</p>
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteHistory(entry.id);
                                }}
                                className="opacity-0 group-hover:opacity-100 transition-opacity text-foreground-300 hover:text-red-400 cursor-pointer shrink-0"
                                title="Eliminar"
                              >
                                <i className="ri-delete-bin-line text-[11px]"></i>
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="px-4 py-3 border-t border-background-200 shrink-0 bg-background-50">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-violet-50 flex items-center justify-center">
                      <i className="ri-information-line text-violet-500 text-xs"></i>
                    </div>
                    <p className="text-foreground-400 text-[9px]">
                      Todo el contenido se genera localmente. Nada se envía a servidores externos.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Mobile bottom hint */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · IA para Radios</p>
      </div>
    </div>
  );
}